/**
 * Production Suite — audio playback engine.
 *
 * Two responsibilities:
 *
 *  1. `synthPreview(type)` — synthesise a short (~2s) musical clip via Web Audio
 *     to demo seed assets that have no real audio_url backing them.
 *
 *  2. `TransportPlayer` — schedules clip playback against the transport's
 *     `currentTime`. Each clip with `audioUrl` is loaded into an HTMLAudioElement;
 *     each clip WITHOUT an audioUrl is rendered as a synthesised tone matching
 *     its track type. Honors mute/solo + per-track volume + master volume.
 *
 * Kept entirely client-side — no backend round-trip for the demo experience.
 */

const TRACK_PRESETS = {
  vo:     { kind: "tone",   wave: "sine",     baseFreq: 320, sweep: -40 },
  bed:    { kind: "chord",  wave: "triangle", baseFreq: 220, notes: [0, 7, 12] },
  fx:     { kind: "noise",  decay: 0.18 },
  jingle: { kind: "arpeggio", wave: "square",  baseFreq: 440, notes: [0, 4, 7, 12], step: 0.08 },
  sfx:    { kind: "noise",  decay: 0.4 },
};

const TYPE_TO_TRACK = {
  Bed:     "bed",
  Voice:   "vo",
  Jingle:  "jingle",
  SFX:     "fx",
  "AI gen": "bed",
};

function getCtx() {
  const AudioCtx = window.AudioContext || window.webkitAudioContext;
  if (!AudioCtx) return null;
  if (!getCtx._inst) getCtx._inst = new AudioCtx();
  // Browsers suspend the context until a user gesture; resume on demand.
  if (getCtx._inst.state === "suspended") getCtx._inst.resume().catch(() => {});
  return getCtx._inst;
}

function scheduleSynth(ctx, trackKey, startAt, duration, gain) {
  const preset = TRACK_PRESETS[trackKey] || TRACK_PRESETS.fx;
  const masterGain = ctx.createGain();
  masterGain.gain.value = gain;
  masterGain.connect(ctx.destination);
  const dur = Math.max(0.05, Math.min(duration, 30));
  const nodes = [];      // every AudioNode we created — needed for stop()

  // Return handle: every scheduleSynth path returns { nodes, masterGain, endsAt }.
  // The public API wraps this into a stop() function callers can invoke to
  // cancel playback mid-flight (e.g. when the user clicks Pause).
  const finish = () => ({ nodes, masterGain, endsAt: startAt + dur });

  if (preset.kind === "tone") {
    const osc = ctx.createOscillator();
    osc.type = preset.wave;
    osc.frequency.setValueAtTime(preset.baseFreq, startAt);
    osc.frequency.linearRampToValueAtTime(preset.baseFreq + (preset.sweep || 0), startAt + dur);
    const env = ctx.createGain();
    env.gain.setValueAtTime(0.0001, startAt);
    env.gain.exponentialRampToValueAtTime(0.5, startAt + 0.04);
    env.gain.setTargetAtTime(0.0, startAt + dur - 0.2, 0.08);
    osc.connect(env); env.connect(masterGain);
    osc.start(startAt); osc.stop(startAt + dur);
    nodes.push(osc, env);
    return finish();
  }
  if (preset.kind === "chord") {
    preset.notes.forEach((semis) => {
      const osc = ctx.createOscillator();
      osc.type = preset.wave;
      osc.frequency.value = preset.baseFreq * Math.pow(2, semis / 12);
      const env = ctx.createGain();
      env.gain.setValueAtTime(0.0001, startAt);
      env.gain.exponentialRampToValueAtTime(0.18, startAt + 0.4);
      env.gain.setTargetAtTime(0.0, startAt + dur - 0.6, 0.25);
      osc.connect(env); env.connect(masterGain);
      osc.start(startAt); osc.stop(startAt + dur);
      nodes.push(osc, env);
    });
    return finish();
  }
  if (preset.kind === "arpeggio") {
    preset.notes.forEach((semis, i) => {
      const t = startAt + i * preset.step;
      if (t > startAt + dur) return;
      const osc = ctx.createOscillator();
      osc.type = preset.wave;
      osc.frequency.value = preset.baseFreq * Math.pow(2, semis / 12);
      const env = ctx.createGain();
      env.gain.setValueAtTime(0.0001, t);
      env.gain.exponentialRampToValueAtTime(0.32, t + 0.012);
      env.gain.exponentialRampToValueAtTime(0.0001, t + preset.step * 0.9);
      osc.connect(env); env.connect(masterGain);
      osc.start(t); osc.stop(t + preset.step + 0.05);
      nodes.push(osc, env);
    });
    return finish();
  }
  // noise — short percussive burst (drums, whoosh, etc.)
  const bufLen = Math.floor(ctx.sampleRate * dur);
  const buf = ctx.createBuffer(1, bufLen, ctx.sampleRate);
  const data = buf.getChannelData(0);
  const decay = preset.decay || 0.3;
  for (let i = 0; i < bufLen; i++) {
    const t = i / ctx.sampleRate;
    data[i] = (Math.random() * 2 - 1) * Math.exp(-t / decay);
  }
  const src = ctx.createBufferSource();
  src.buffer = buf;
  src.connect(masterGain);
  src.start(startAt);
  nodes.push(src);
  return finish();
}

// ────────────────────────────────────────────────────────────────────────────
// Public API
// ────────────────────────────────────────────────────────────────────────────

/** Play a short synthesised preview for a seed asset (or any asset without audio_url).
 *  Returns a `stop()` function callers can invoke to abort playback immediately
 *  (e.g. when the user clicks Pause). If WebAudio isn't available, returns a
 *  no-op stop function so callers can call it unconditionally. */
export function synthPreview(assetType) {
  const ctx = getCtx();
  if (!ctx) return () => {};
  const trackKey = TYPE_TO_TRACK[assetType] || "fx";
  const handle = scheduleSynth(ctx, trackKey, ctx.currentTime + 0.02, 1.8, 0.55);
  return () => {
    // Ramp master gain down fast to avoid pops, then hard-stop everything.
    try {
      const t = ctx.currentTime;
      handle.masterGain.gain.cancelScheduledValues(t);
      handle.masterGain.gain.setValueAtTime(handle.masterGain.gain.value, t);
      handle.masterGain.gain.linearRampToValueAtTime(0.0001, t + 0.03);
    } catch { /* noop */ }
    handle.nodes.forEach((n) => {
      try { if (typeof n.stop === "function") n.stop(ctx.currentTime + 0.04); } catch { /* noop */ }
      try { n.disconnect(); } catch { /* noop */ }
    });
    try { handle.masterGain.disconnect(); } catch { /* noop */ }
  };
}

/**
 * TransportPlayer — drives audio for the timeline.
 *
 * Usage:
 *   const player = new TransportPlayer();
 *   player.start(clips, { tracks, volumes, getCurrentTime });
 *   // ...later
 *   player.stop();
 */
export class TransportPlayer {
  constructor() {
    this.audios = new Map();    // clipId → HTMLAudioElement
    this.scheduled = new Map(); // clipId → { nodes, masterGain, endsAt }
    this.tickHandle = null;
    this.startedAt = 0;
    this.startedFrom = 0;
    this.ctx = null;
  }

  /** Begin playback. `getState()` returns the latest { clips, tracks, volumes, master } each tick. */
  start(getState, currentTime = 0) {
    this.stop();
    this.ctx = getCtx();
    if (!this.ctx) return;
    this.startedAt = this.ctx.currentTime;
    this.startedFrom = currentTime;

    // Pre-create audio elements for clips with real URLs.
    // NOTE: no `crossOrigin = "anonymous"` — audio URLs are same-origin and
    // require the session cookie for auth. Setting anonymous strips
    // credentials in some Chromium builds → 401 → silent playback failure.
    const initial = getState();
    initial.clips.forEach((c) => {
      if (c.audioUrl && !this.audios.has(c.id)) {
        const a = new Audio(c.audioUrl);
        a.preload = "auto";
        this.audios.set(c.id, a);
      }
    });

    const tick = () => {
      if (!this.ctx) return;
      const state = getState();
      const t = state.currentTime;
      // mute/solo arrive as { trackId: bool } objects from React state (not on the tracks array).
      const muteMap = state.mute || {};
      const soloMap = state.solo || {};
      const anySolo = Object.values(soloMap).some(Boolean);

      (state.clips || []).forEach((c) => {
        const trackId = c.track;
        const tr = (state.tracks || []).find((x) => x.id === trackId);
        if (!tr) return;
        const muted = !!muteMap[tr.id] || (anySolo && !soloMap[tr.id]);
        const inWindow = t >= c.start && t < c.start + c.duration;

        const trackVol = ((state.volumes || {})[trackId] ?? 75) / 100;
        const masterVol = ((state.volumes || {}).master ?? 80) / 100;
        const gain = muted ? 0 : trackVol * masterVol;

        if (c.audioUrl) {
          // Real audio
          const a = this.audios.get(c.id);
          if (!a) return;
          if (inWindow) {
            const offset = t - c.start;
            if (a.paused) {
              if (Math.abs(a.currentTime - offset) > 0.25) {
                try { a.currentTime = Math.max(0, offset); } catch { /* seek blocked */ }
              }
              a.volume = Math.max(0, Math.min(1, gain));
              a.play().catch(() => { /* autoplay blocked until user gesture */ });
            } else {
              a.volume = Math.max(0, Math.min(1, gain));
            }
          } else if (!a.paused) {
            a.pause();
          }
        } else {
          // Synth — schedule once when playhead first enters window
          if (inWindow && !this.scheduled.has(c.id) && gain > 0.001) {
            const handle = scheduleSynth(this.ctx, trackId, this.ctx.currentTime, c.duration - (t - c.start), gain);
            this.scheduled.set(c.id, handle);
          }
          if (!inWindow && this.scheduled.has(c.id)) {
            // Playhead moved past this synth clip's window. Silence its
            // oscillators/buffers so re-triggers don't overlap and the
            // scheduled tail never rings on beyond its clip duration.
            this._silenceHandle(this.scheduled.get(c.id));
            this.scheduled.delete(c.id);
          }
        }
      });

      this.tickHandle = requestAnimationFrame(tick);
    };
    tick();
  }

  stop() {
    if (this.tickHandle) cancelAnimationFrame(this.tickHandle);
    this.tickHandle = null;
    this.audios.forEach((a) => { try { a.pause(); a.currentTime = 0; } catch { /* noop */ } });
    // Kill every scheduled WebAudio synth clip so nothing rings on after Stop.
    this.scheduled.forEach((handle) => this._silenceHandle(handle));
    this.scheduled.clear();
  }

  /** Lightweight pause — keeps audio elements in place but halts synth scheduling. */
  pause() {
    if (this.tickHandle) cancelAnimationFrame(this.tickHandle);
    this.tickHandle = null;
    this.audios.forEach((a) => { try { a.pause(); } catch { /* noop */ } });
    // Silence any in-flight synth clips too — Pause should mean silence,
    // not "pause the transport but the reverb tail keeps ringing".
    this.scheduled.forEach((handle) => this._silenceHandle(handle));
    this.scheduled.clear();
  }

  /** Kill a single scheduleSynth() handle — ramps master to zero, hard-stops
   *  every source node, then disconnects the entire subgraph. */
  _silenceHandle(handle) {
    if (!handle || !this.ctx) return;
    try {
      const t = this.ctx.currentTime;
      handle.masterGain.gain.cancelScheduledValues(t);
      handle.masterGain.gain.setValueAtTime(handle.masterGain.gain.value, t);
      handle.masterGain.gain.linearRampToValueAtTime(0.0001, t + 0.03);
    } catch { /* noop */ }
    (handle.nodes || []).forEach((n) => {
      try { if (typeof n.stop === "function") n.stop(this.ctx.currentTime + 0.04); } catch { /* noop */ }
      try { n.disconnect(); } catch { /* noop */ }
    });
    try { handle.masterGain.disconnect(); } catch { /* noop */ }
  }
}
